import { Theme } from '@logto/schemas';
import type { TFuncKey } from 'i18next';
import { useContext } from 'react';

import StaticPageLayout from '@/Layout/StaticPageLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import EmptyStateDark from '@/assets/icons/empty-state-dark.svg?react';
import EmptyState from '@/assets/icons/empty-state.svg?react';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import DynamicT from '@/shared/components/DynamicT';
import NavBar from '@/shared/components/NavBar';
import PageMeta from '@/shared/components/PageMeta';

import SupportInfo from './SupportInfo';
import styles from './index.module.scss';

type Props = {
  readonly title?: TFuncKey;
  readonly message?: TFuncKey;
  readonly rawMessage?: string;
  readonly isNavbarHidden?: boolean;
  readonly backNavigation?: 'history' | 'unknownSessionRedirectUrl';
};

const ErrorPage = ({
  title = 'description.not_found',
  message,
  rawMessage,
  isNavbarHidden,
  backNavigation = 'history',
}: Props) => {
  const { theme, experienceSettings } = useContext(PageContext);
  const navigate = useNavigateWithPreservedSearchParams();
  const errorMessage = Boolean(rawMessage ?? message);
  const unknownSessionRedirectUrl =
    backNavigation === 'unknownSessionRedirectUrl'
      ? experienceSettings?.unknownSessionRedirectUrl
      : undefined;
  const shouldShowNavbar =
    Boolean(unknownSessionRedirectUrl) || (backNavigation === 'history' && history.length > 1);

  const onBack = () => {
    if (unknownSessionRedirectUrl) {
      window.location.assign(unknownSessionRedirectUrl);

      return;
    }

    navigate(-1);
  };

  return (
    <StaticPageLayout>
      <PageMeta titleKey={title} />
      {shouldShowNavbar && <NavBar isHidden={isNavbarHidden} onBack={onBack} />}
      <div className={styles.container}>
        {theme === Theme.Light ? <EmptyState /> : <EmptyStateDark />}
        <div className={styles.title}>
          <DynamicT forKey={title} />
        </div>
        {errorMessage && (
          <div className={styles.message}>{rawMessage ?? <DynamicT forKey={message} />}</div>
        )}
        <SupportInfo />
      </div>
    </StaticPageLayout>
  );
};

export default ErrorPage;
